import { useState, useEffect, useMemo, useRef, type ReactNode } from "react";
import { db, storage, functions } from "../firebaseConfig";
import { httpsCallable } from "firebase/functions";
import { doc, updateDoc, deleteField } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { AlertTriangle, CheckCircle2, ImageIcon, MapPin, Package, Plus, PowerOff, Search, Trash2, X } from "lucide-react";

import Header from "../components/HeaderV2";
import common from "../style/screens/CommonV2.module.css";
import style from "../style/screens/KioskV2.module.css";
import ErrMsg from "../ErrMsg";
import { AddressInput, BlockInput, ToggleSwitch, PhotoInput, SelectorInput, photoResize } from "../components/Inputs";
import { TextButton } from "../components/Buttons";
import { KioskType, useKiosk } from "../KioskContext";
import { usePartner } from "../PartnerContext";
import KioskOperationModeInput from "../components/KioskOperationModeInput";
import CollectionHistoryModal from "../components/CollectionHistoryModal";
import RegisterKiosksModal from "../components/RegisterKiosksModal";
import { resolveOperationMode, operationModeLabel, type KioskOperationMode } from "../utils/kioskOperation";

type KioskDisplayStatus = KioskType["status"] | "NEAR_FULL";
type KioskFilter = "ALL" | KioskDisplayStatus;
type KioskViewMode = "LIST" | "MAP";
type Coordinates = { lat: number; lng: number };
const KAKAO_MAP_APP_KEY = process.env.REACT_APP_KAKAO_MAP_APP_KEY || "2a3983a0b8a07aee5ac46793aa95d48e";
const CAPACITY_WARNING_RATE = 50;
const CAPACITY_DANGER_RATE = 80;

const parsePositiveInteger = (value: string): number | null => {
    const parsed = Number(value.trim());
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const parseOptionalNonNegativeNumber = (value: string): number | null => {
    if (!value.trim()) return null;
    const parsed = Number(value.trim());
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
};

declare global {
    interface Window {
        kakao?: any;
    }
}

let kakaoMapLoader: Promise<any> | null = null;

const getValidCoordinates = (lat: unknown, lng: unknown): Coordinates | null => {
    if (typeof lat !== "number" || typeof lng !== "number") return null;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
    if (lat === 0 && lng === 0) return null;
    return { lat, lng };
};

const parseCoordinateInput = (latitude: string, longitude: string): Coordinates | null => {
    if (!latitude.trim() || !longitude.trim()) return null;
    return getValidCoordinates(Number(latitude), Number(longitude));
};

const hasKioskCoordinates = (kiosk: KioskType) => Boolean(getValidCoordinates(kiosk.lat, kiosk.lng));

const Kiosk = () => {
    const { kiosks, pendings } = useKiosk();
    const { partner } = usePartner();
    const [registerOpen, setRegisterOpen] = useState(false);
    const [registrationMessage, setRegistrationMessage] = useState("");
    const [editKiosk, setEditKiosk] = useState<boolean>(false);
    const [target, setTarget] = useState<KioskType | null>(null);
    const [required, setRequired] = useState<boolean>(false);
    const [statusFilter, setStatusFilter] = useState<KioskFilter>("ALL");
    const [viewMode, setViewMode] = useState<KioskViewMode>("LIST");
    const [selectedPartnerId, setSelectedPartnerId] = useState<string>("ALL");
    const [historyKioskId, setHistoryKioskId] = useState<string | null>(null);
    const historyKiosk = kiosks.find((item) => item.docId === historyKioskId);

    useEffect(() => {
        document.body.style.overflow = editKiosk || historyKioskId ? "hidden" : "auto";
    }, [editKiosk, historyKioskId]);

    return (
        <>
            <Header />
            <div className={common["container"]}>
                <div className={common["wrapper"]}>
                    <div className={style["page-heading"]}>
                        <h4 className={common["text--title"]}>키오스크 관리</h4>
                        <button type="button" className={style["register-button"]} onClick={() => setRegisterOpen(true)}><Plus size={18} />기기 추가</button>
                    </div>
                    <p className={common["text--copy"]}>
                        전체 키오스크 상태를 확인하고 앱 노출, 수거 처리, 기본 정보를 관리합니다.
                    </p>

                    {registrationMessage && <div role="status" className={style["registration-message"]}>
                        <span>{registrationMessage}</span>
                        <button type="button" aria-label="등록 완료 안내 닫기" onClick={() => setRegistrationMessage("")}><X size={16} /></button>
                    </div>}

                    <KioskSummary
                        kiosks={kiosks}
                        pendingCount={pendings.length}
                        activeFilter={statusFilter}
                        setFilter={setStatusFilter}
                    />

                    <div className={style["view-switch"]}>
                        <button
                            type="button"
                            className={viewMode === "LIST" ? style["view-switch--active"] : ""}
                            onClick={() => setViewMode("LIST")}
                        >
                            목록
                        </button>
                        <button
                            type="button"
                            className={viewMode === "MAP" ? style["view-switch--active"] : ""}
                            onClick={() => setViewMode("MAP")}
                        >
                            지도
                        </button>
                    </div>

                    {viewMode === "LIST" ? (
                        <>
                            <PendingKiosk
                                activeFilter={statusFilter}
                                setRequired={() => setRequired(true)}
                                setKiosk={(kiosk: KioskType) => setTarget(kiosk)}
                                edit={() => setEditKiosk(true)}
                            />
                            <KioskTable
                                showHistory={setHistoryKioskId}
                                activeFilter={statusFilter}
                                selectedPartnerId={selectedPartnerId}
                                setSelectedPartnerId={setSelectedPartnerId}
                                setRequired={() => setRequired(false)}
                                setKiosk={(kiosk: KioskType) => setTarget(kiosk)}
                                edit={() => setEditKiosk(true)}
                            />
                        </>
                    ) : (
                        <KioskMapPanel
                            kiosks={kiosks}
                            selectedPartnerId={selectedPartnerId}
                            setSelectedPartnerId={setSelectedPartnerId}
                        />
                    )}
                </div>
            </div>

            {registerOpen && <RegisterKiosksModal partners={partner} initialPartnerId={selectedPartnerId} close={() => setRegisterOpen(false)} onRegistered={(partnerId, count) => {
                setRegisterOpen(false);
                setViewMode("LIST");
                setStatusFilter("PENDING");
                setSelectedPartnerId(partnerId);
                setRegistrationMessage(`${partner.find(item => item.id === partnerId)?.name || partnerId}에 기기 ${count}대를 추가했습니다. 등록 대기 목록에서 기본 정보를 입력하고 승인해주세요.`);
            }} />}
            {editKiosk && (
                <EditKiosk required={required} kiosk={target as KioskType} close={() => setEditKiosk(false)} />
            )}
            {historyKiosk && <CollectionHistoryModal target={{ kind: "kiosk", kiosk: historyKiosk }} close={() => setHistoryKioskId(null)} />}
        </>
    );
};

const KioskMapPanel = ({
    kiosks,
    selectedPartnerId,
    setSelectedPartnerId,
}: {
    kiosks: KioskType[];
    selectedPartnerId: string;
    setSelectedPartnerId: (partnerId: string) => void;
}) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const mapRef = useRef<any>(null);
    const markersRef = useRef<any[]>([]);
    const infoWindowsRef = useRef<any[]>([]);
    const [mapReady, setMapReady] = useState<boolean>(false);
    const [mapError, setMapError] = useState<string>("");

    const filteredKiosks = useMemo(
        () => kiosks.filter((kiosk) => selectedPartnerId === "ALL" || kiosk.partnerId === selectedPartnerId),
        [kiosks, selectedPartnerId]
    );
    const positionedKiosks = useMemo(
        () => filteredKiosks.filter(hasKioskCoordinates),
        [filteredKiosks]
    );

    useEffect(() => {
        if (!KAKAO_MAP_APP_KEY) {
            setMapError("카카오맵 앱 키가 설정되지 않았습니다. REACT_APP_KAKAO_MAP_APP_KEY를 추가해주세요.");
            return;
        }
        if (!containerRef.current) return;

        let cancelled = false;

        loadKakaoMap(KAKAO_MAP_APP_KEY)
            .then((kakao) => {
                if (cancelled || !containerRef.current) return;

                const center = positionedKiosks[0]
                    ? new kakao.maps.LatLng(positionedKiosks[0].lat, positionedKiosks[0].lng)
                    : new kakao.maps.LatLng(37.5665, 126.978);

                if (!mapRef.current) {
                    mapRef.current = new kakao.maps.Map(containerRef.current, {
                        center,
                        level: positionedKiosks.length > 0 ? 5 : 8,
                    });
                } else {
                    mapRef.current.relayout();
                    mapRef.current.setCenter(center);
                }

                markersRef.current.forEach((marker) => marker.setMap(null));
                infoWindowsRef.current.forEach((infoWindow) => infoWindow.setMap(null));
                markersRef.current = [];
                infoWindowsRef.current = [];

                if (positionedKiosks.length > 0) {
                    const bounds = new kakao.maps.LatLngBounds();

                    positionedKiosks.forEach((kiosk) => {
                        const position = new kakao.maps.LatLng(kiosk.lat, kiosk.lng);
                        const marker = new kakao.maps.CustomOverlay({
                            map: mapRef.current,
                            position,
                            content: getMarkerContent(kiosk),
                            yAnchor: 1,
                        });
                        let infoWindow: any;
                        const infoWindowContent = getInfoWindowContent(kiosk, () => infoWindow?.setMap(null));
                        infoWindow = new kakao.maps.CustomOverlay({
                            position,
                            content: infoWindowContent,
                            xAnchor: 0.5,
                            yAnchor: 1.42,
                            zIndex: 4,
                        });

                        const markerElement = marker.getContent();
                        markerElement.addEventListener("click", () => {
                            infoWindowsRef.current.forEach((item) => item.setMap(null));
                            infoWindow.setMap(mapRef.current);
                        });

                        bounds.extend(position);
                        markersRef.current.push(marker);
                        infoWindowsRef.current.push(infoWindow);
                    });

                    if (positionedKiosks.length === 1) {
                        mapRef.current.setCenter(new kakao.maps.LatLng(positionedKiosks[0].lat, positionedKiosks[0].lng));
                        mapRef.current.setLevel(4);
                    } else {
                        mapRef.current.setBounds(bounds);
                    }
                }

                setMapReady(true);
                setMapError("");
            })
            .catch((error) => {
                console.error(error);
                setMapError("카카오맵을 불러오지 못했습니다. 앱 키와 도메인 설정을 확인해주세요.");
            });

        return () => {
            cancelled = true;
        };
    }, [positionedKiosks]);

    return (
        <section className={style["panel"]}>
            <div className={style["panel-header"]}>
                <div>
                    <h5 className={style["panel-title"]}>키오스크 위치</h5>
                    <p className={style["panel-copy"]}>
                        좌표가 등록된 키오스크 {positionedKiosks.length}개 / 선택된 키오스크 {filteredKiosks.length}개
                    </p>
                </div>
                <span className={style["badge"]}>Kakao Map</span>
            </div>
            <div className={style["map-legend"]}>
                <LegendItem status="ACTIVE" label="운영중" />
                <LegendItem status="NEAR_FULL" label="수거 임박" />
                <LegendItem status="INACTIVE" label="운영중지" />
                <LegendItem status="FULL" label="수거 필요" />
            </div>
            <PartnerFilter
                kiosks={kiosks}
                selectedPartnerId={selectedPartnerId}
                setSelectedPartnerId={setSelectedPartnerId}
            />
            <div className={style["map-shell"]}>
                <div ref={containerRef} className={style["kakao-map"]} />
                {(!mapReady || mapError || positionedKiosks.length === 0) && (
                    <div className={style["map-overlay"]}>
                        <MapPin size={22} />
                        <strong>{mapError || "표시할 위치 정보가 없습니다."}</strong>
                        <span>
                            {!mapError && positionedKiosks.length === 0
                                ? "키오스크 정보에 위도와 경도가 등록되면 지도에 핀이 표시됩니다."
                                : "카카오 개발자 콘솔의 JavaScript 키와 Web 플랫폼 도메인을 확인하세요."}
                        </span>
                    </div>
                )}
            </div>
        </section>
    );
};

const LegendItem = ({ status, label }: { status: KioskDisplayStatus; label: string }) => (
    <span className={style["legend-item"]}>
        <span className={[style["legend-dot"], style[`legend-dot--${getMarkerStatusClass(status)}`]].join(" ")} />
        {label}
    </span>
);

const PartnerFilter = ({
    kiosks,
    selectedPartnerId,
    setSelectedPartnerId,
}: {
    kiosks: KioskType[];
    selectedPartnerId: string;
    setSelectedPartnerId: (partnerId: string) => void;
}) => {
    const { partner } = usePartner();
    const partnerFilters = useMemo(
        () =>
            [...partner].sort((a, b) =>
                (a.name || a.id || "").localeCompare(b.name || b.id || "", "ko")
            ),
        [partner]
    );
    const partnerKioskCounts = useMemo(
        () =>
            kiosks.reduce<Record<string, number>>((counts, kiosk) => {
                if (kiosk.partnerId) {
                    counts[kiosk.partnerId] = (counts[kiosk.partnerId] || 0) + 1;
                }
                return counts;
            }, {}),
        [kiosks]
    );

    return (
        <div className={style["partner-filter"]} role="group" aria-label="파트너별 키오스크 필터">
            <button
                type="button"
                className={selectedPartnerId === "ALL" ? style["partner-filter--active"] : ""}
                aria-pressed={selectedPartnerId === "ALL"}
                onClick={() => setSelectedPartnerId("ALL")}
            >
                전체({kiosks.length.toLocaleString("ko-KR")})
            </button>
            {partnerFilters.map((item) => (
                <button
                    key={item.docId || item.id}
                    type="button"
                    className={selectedPartnerId === item.id ? style["partner-filter--active"] : ""}
                    aria-pressed={selectedPartnerId === item.id}
                    onClick={() => setSelectedPartnerId(item.id)}
                >
                    {item.name || item.id}({(partnerKioskCounts[item.id] || 0).toLocaleString("ko-KR")})
                </button>
            ))}
        </div>
    );
};

const getKioskFillRate = (kiosk: KioskType) => {
    const maxCapacity = Number(kiosk.maxCapacity ?? 0);
    const currentCapacity = Number(kiosk.currentCapacity ?? 0);
    return maxCapacity > 0 ? Math.min(Math.round((currentCapacity / maxCapacity) * 100), 100) : 0;
};

const getKioskDisplayStatus = (kiosk: KioskType): KioskDisplayStatus => {
    if (kiosk.status === "PENDING" || kiosk.status === "INACTIVE" || kiosk.status === "FULL") return kiosk.status;

    const fillRate = getKioskFillRate(kiosk);
    if (fillRate >= CAPACITY_DANGER_RATE) return "FULL";
    if (fillRate >= CAPACITY_WARNING_RATE) return "NEAR_FULL";
    return kiosk.status;
};

const KioskSummary = ({
    kiosks,
    pendingCount,
    activeFilter,
    setFilter,
}: {
    kiosks: KioskType[];
    pendingCount: number;
    activeFilter: KioskFilter;
    setFilter: (filter: KioskFilter) => void;
}) => {
    const activeCount = kiosks.filter((kiosk) => getKioskDisplayStatus(kiosk) === "ACTIVE").length;
    const inactiveCount = kiosks.filter((kiosk) => getKioskDisplayStatus(kiosk) === "INACTIVE").length;
    const warningCount = kiosks.filter((kiosk) => getKioskDisplayStatus(kiosk) === "NEAR_FULL").length;
    const fullCount = kiosks.filter((kiosk) => getKioskDisplayStatus(kiosk) === "FULL").length;

    return (
        <div className={style["summary-grid"]}>
            <SummaryCard
                label="전체 키오스크"
                value={kiosks.length}
                icon={<MapPin size={20} />}
                active={activeFilter === "ALL"}
                onClick={() => setFilter("ALL")}
            />
            <SummaryCard
                label="운영중"
                value={activeCount}
                icon={<CheckCircle2 size={20} />}
                tone="success"
                active={activeFilter === "ACTIVE"}
                onClick={() => setFilter("ACTIVE")}
            />
            <SummaryCard
                label="운영중지"
                value={inactiveCount}
                icon={<PowerOff size={20} />}
                active={activeFilter === "INACTIVE"}
                onClick={() => setFilter("INACTIVE")}
            />
            <SummaryCard
                label="수거 임박"
                value={warningCount}
                icon={<AlertTriangle size={20} />}
                tone="warning"
                active={activeFilter === "NEAR_FULL"}
                onClick={() => setFilter("NEAR_FULL")}
            />
            <SummaryCard
                label="수거 필요"
                value={fullCount}
                icon={<AlertTriangle size={20} />}
                tone="danger"
                active={activeFilter === "FULL"}
                onClick={() => setFilter("FULL")}
            />
            <SummaryCard
                label="등록 대기"
                value={pendingCount}
                icon={<Package size={20} />}
                tone="info"
                active={activeFilter === "PENDING"}
                onClick={() => setFilter("PENDING")}
            />
        </div>
    );
};

const SummaryCard = ({
    label,
    value,
    icon,
    active,
    onClick,
    tone = "default",
}: {
    label: string;
    value: number;
    icon: ReactNode;
    active: boolean;
    onClick: () => void;
    tone?: "default" | "success" | "warning" | "danger" | "info";
}) => (
    <button
        type="button"
        className={[style["summary-card"], style[`summary-card--${tone}`], active && style["summary-card--active"]]
            .filter(Boolean)
            .join(" ")}
        onClick={onClick}
    >
        <div>
            <p className={style["summary-label"]}>{label}</p>
            <p className={style["summary-value"]}>{value}</p>
        </div>
        <div className={style["summary-icon"]}>{icon}</div>
    </button>
);

const PendingKiosk = ({
    activeFilter,
    setRequired,
    setKiosk,
    edit,
}: {
    activeFilter: KioskFilter;
    setRequired: () => void;
    setKiosk: (kiosk: KioskType) => void;
    edit: () => void;
}) => {
    const { pendings } = useKiosk();

    const editInfo = (target: KioskType) => {
        setRequired();
        edit();
        setKiosk(target);
    };

    return (
        <>
            {(activeFilter === "ALL" || activeFilter === "PENDING") && pendings.length > 0 && (
                <section className={style["panel"]}>
                    <div className={style["panel-header"]}>
                        <h5 className={style["panel-title"]}>새로 생성된 키오스크</h5>
                        <span className={style["badge"]}>{pendings.length}개 대기</span>
                    </div>
                    <div className={style["wrapper--kiosk-pending"]}>
                        {pendings.map((kiosk, index) => (
                            <div
                                key={index}
                                className={[style["unit--kiosk-list"], style["unit--kiosk-list--pending"]].join(" ")}
                                onClick={() => editInfo(kiosk)}
                            >
                                <div
                                    className={[style["container--rows"], style["container--rows--pending"]].join(" ")}
                                >
                                    <div>
                                        <p className={style["kiosk-name"]}>{kiosk.id}</p>
                                        <p className={style["kiosk-meta"]}>기본 정보를 입력하고 등록을 승인해주세요.</p>
                                    </div>
                                    <TextButton text="정보 입력하기" onClick={() => editInfo(kiosk)} />
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}
        </>
    );
};

const KioskTable = ({
    showHistory,
    activeFilter,
    selectedPartnerId,
    setSelectedPartnerId,
    setRequired,
    setKiosk,
    edit,
}: {
    showHistory: (kioskId: string) => void;
    activeFilter: KioskFilter;
    selectedPartnerId: string;
    setSelectedPartnerId: (partnerId: string) => void;
    setRequired: () => void;
    setKiosk: (kiosk: KioskType) => void;
    edit: () => void;
}) => {
    const { kiosks } = useKiosk();
    // 파트너 목록(이름 매핑용)
    const { partner } = usePartner();

    const [search, setSearch] = useState<string>("");
    // 정렬 상태: 기본, 가나다순, 최신순
    const [sortBy, setSortBy] = useState<"DEFAULT" | "ALPHA" | "NEWEST" | "FULL_FIRST">("DEFAULT");

    const editInfo = (kiosk: KioskType) => {
        setRequired();
        edit();
        setKiosk(kiosk);
    };

    const [loadingConfirmMap, setLoadingConfirmMap] = useState<Record<string, boolean>>({});
    const [disableConfirmMap, setDisableConfirmMap] = useState<Record<string, boolean>>({});

    const toggleKiosk = async (kiosk: KioskType) => {
        setDisableConfirmMap((prev) => ({ ...prev, [kiosk.docId]: true }));

        return updateDoc(doc(db, "kiosk", kiosk.docId), {
            status: kiosk.status === "INACTIVE" ? "ACTIVE" : "INACTIVE",
        })
            .then(() => {
                setDisableConfirmMap((prev) => ({
                    ...prev,
                    [kiosk.docId]: false,
                }));
            })
            .catch((err) => {
                setDisableConfirmMap((prev) => ({
                    ...prev,
                    [kiosk.docId]: false,
                }));
                console.error(err);
            });
    };

    const emptyKiosk = async (kiosk: KioskType) => {
        setLoadingConfirmMap((prev) => ({ ...prev, [kiosk.docId]: true }));

        return httpsCallable(
            functions,
            "emptyKiosk"
        )({
            docId: kiosk.docId,
        })
            .then((result) => {
                setLoadingConfirmMap((prev) => ({
                    ...prev,
                    [kiosk.docId]: false,
                }));
                alert("수거가 완료되었습니다.");
            })
            .catch((err) => {
                setLoadingConfirmMap((prev) => ({
                    ...prev,
                    [kiosk.docId]: false,
                }));
                console.error(err);
                alert(ErrMsg[err.message] || "에러가 발생했습니다");
            });
    };

    // 필터링된 목록 (검색 + 상태 필터)
    const baseFiltered = useMemo(() => {
        const keyword = search.toLowerCase();
        return kiosks
            .filter(
                (kiosk) =>
                    activeFilter === "ALL" ||
                    activeFilter === "PENDING" ||
                    getKioskDisplayStatus(kiosk) === activeFilter
            )
            .filter((kiosk) => selectedPartnerId === "ALL" || kiosk.partnerId === selectedPartnerId)
            .filter((kiosk) => {
                return (
                    search === "" ||
                    kiosk.id.toLowerCase().includes(keyword) ||
                    (kiosk.name ?? "").toLowerCase().includes(keyword) ||
                    (kiosk.address ?? "").toLowerCase().includes(keyword)
                );
            });
    }, [kiosks, activeFilter, selectedPartnerId, search]);

    // 정렬 적용
    const filteredKiosks = useMemo(() => {
        if (sortBy === "ALPHA") {
            return [...baseFiltered].sort((a, b) => {
                // @ts-ignore
                const an = (a.name || a.id || "");
                const bn = (b.name || b.id || "");
                // 한글/영문 모두 자연스러운 로케일 비교
                return an.localeCompare(bn, "ko");
            });
        }

        if (sortBy === "NEWEST") {
            return [...baseFiltered].sort((a, b) => Number(b.timestamp ?? 0) - Number(a.timestamp ?? 0));
        }

        if (sortBy === "FULL_FIRST") {
            return [...baseFiltered].sort((a, b) => {
                const aMax = Number(a.maxCapacity ?? 0) || 0;
                const bMax = Number(b.maxCapacity ?? 0) || 0;
                const aCur = Number(a.currentCapacity ?? 0) || 0;
                const bCur = Number(b.currentCapacity ?? 0) || 0;
                const aRate = aMax > 0 ? aCur / aMax : 0;
                const bRate = bMax > 0 ? bCur / bMax : 0;
                if (bRate !== aRate) return bRate - aRate; // 높은 채움 비율 우선
                // 동률인 경우에는 상태가 FULL인 것을 우선 (안정성)
                if ((getKioskDisplayStatus(b) === "FULL") !== (getKioskDisplayStatus(a) === "FULL")) {
                    return getKioskDisplayStatus(b) === "FULL" ? 1 : -1;
                }
                // 그래도 같으면 최신순
                return Number(b.timestamp ?? 0) - Number(a.timestamp ?? 0);
            });
        }

        return baseFiltered;
    }, [baseFiltered, sortBy]);

    const currentFilterLabel = getFilterLabel(activeFilter);

    return (
        <>
            {kiosks && activeFilter !== "PENDING" && (
                <section className={style["panel"]}>
                    <div className={style["panel-header"]}>
                        <h5 className={style["panel-title"]}>키오스크 목록 · {currentFilterLabel}</h5>
                        <span className={style["badge"]}>{filteredKiosks.length}개</span>
                    </div>
                    <div className={style["toolbar"]}>
                        <div className={style["search-wrap"]}>
                            <Search size={16} />
                            <input
                                id="search-kiosk"
                                className={common["input--kiosk-filter"]}
                                type="text"
                                placeholder="아이디, 이름 또는 주소로 검색"
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                }}
                            />
                        </div>
                    </div>
                    <div className={style["kiosk-sort"]}>
                        <button
                            type="button"
                            className={sortBy === "DEFAULT" ? style["kiosk-sort--active"] : ""}
                            onClick={() => setSortBy("DEFAULT")}
                        >
                            기본
                        </button>
                        <button
                            type="button"
                            className={sortBy === "ALPHA" ? style["kiosk-sort--active"] : ""}
                            onClick={() => setSortBy("ALPHA")}
                        >
                            가나다순
                        </button>
                        <button
                            type="button"
                            className={sortBy === "NEWEST" ? style["kiosk-sort--active"] : ""}
                            onClick={() => setSortBy("NEWEST")}
                        >
                            최신순
                        </button>
                        <button
                            type="button"
                            className={sortBy === "FULL_FIRST" ? style["kiosk-sort--active"] : ""}
                            onClick={() => setSortBy("FULL_FIRST")}
                        >
                            수거 우선
                        </button>
                    </div>
                    <PartnerFilter
                        kiosks={kiosks}
                        selectedPartnerId={selectedPartnerId}
                        setSelectedPartnerId={setSelectedPartnerId}
                    />
                    <div className={style["wrapper--kiosk-list"]}>
                        {filteredKiosks.length === 0 && (
                            <div className={style["empty-state"]}>선택한 조건의 키오스크가 없습니다.</div>
                        )}
                        {filteredKiosks.map((kiosk, index) => {
                            const maxCapacity = Number(kiosk.maxCapacity ?? 0);
                            const currentCapacity = Number(kiosk.currentCapacity ?? 0);
                            const fillRate = getKioskFillRate(kiosk);
                            const displayStatus = getKioskDisplayStatus(kiosk);
                            // progress color: 초록 | 노랑 | 빨강
                            const progressClass =
                                fillRate < CAPACITY_WARNING_RATE
                                    ? style["progress-bar--good"]
                                    : fillRate < CAPACITY_DANGER_RATE
                                    ? style["progress-bar--warn"]
                                    : style["progress-bar--danger"];

                            return (
                                <div key={index} className={style["unit--kiosk-list"]}>
                                    <div
                                        className={style["kiosk-main"]}
                                        onClick={(e) => {
                                            if (
                                                (e.target instanceof HTMLInputElement && e.target.type === "checkbox") ||
                                                e.target instanceof HTMLSpanElement ||
                                                e.target instanceof HTMLButtonElement
                                            )
                                                return;
                                            editInfo(kiosk);
                                        }}
                                    >
                                        <div className={style["kiosk-head"]}>
                                            <div className={style["kiosk-wrap"]}>
                                                <div className={style["kiosk-title-row"]}>
                                                    <div className={style["kiosk-title-left"]}>
                                                        <p className={style["kiosk-name"]}>{kiosk.name || "이름 없음"}</p>
                                                        <StatusBadge status={displayStatus} />
                                                        <span className={style["badge"]}>{kiosk.id}</span>
                                                        <span className={style["badge"]}>{operationModeLabel(kiosk.operationMode)}</span>
                                                        {/* partner 이름 노출 */}
                                                        {kiosk.partnerId && (
                                                            <span className={style["partner-id"]}>{
                                                                partner.find((p) => p.id === kiosk.partnerId)?.name || kiosk.partnerId
                                                            }</span>
                                                        )}
                                                        {/* pointPerPet 노출 */}
                                                        {((kiosk as any).pointPerPet !== undefined) && (
                                                            <span className={style["point-badge"]}>{(kiosk as any).pointPerPet}</span>
                                                        )}
                                                    </div>
                                                    <div className={style["kiosk-created"]}>
                                                        <span className={style["kiosk-created-label"]}>생성일</span>
                                                        <span className={style["kiosk-created-date"]}>{formatDateTime(kiosk.timestamp)}</span>
                                                    </div>
                                                </div>
                                                <p className={style["kiosk-meta"]}>{kiosk.address || "주소 없음"}</p>
                                                <p className={style["kiosk-doc"]}>{kiosk.docId}</p>
                                            </div>
                                        </div>
                                        <div className={style["capacity-row"]}>
                                            <div className={style["capacity-info"]}>
                                                <span>현재 투입량</span>
                                                <div className={style["capacity-actions"]}><strong>
                                                    {currentCapacity.toLocaleString()} / {maxCapacity ? maxCapacity.toLocaleString() : "-"}
                                                </strong>
                                                <button type="button" className={style["history-button"]} onClick={(event) => { event.stopPropagation(); showHistory(kiosk.docId); }}>투입 내역</button></div>
                                            </div>
                                            <div className={style["progress"]}>
                                                <span className={progressClass} style={{ width: `${fillRate}%` }} aria-label={`채워짐 ${fillRate}%`} />
                                            </div>
                                        </div>
                                    </div>
                                    <div className={style["kiosk-actions"]}>
                                        <div className={style["kiosk-collect-stack"]}>
                                            <CollectButton
                                                loading={loadingConfirmMap[kiosk.docId] ?? false}
                                                onClick={() => emptyKiosk(kiosk)}
                                            />
                                            <CoordinateBadge kiosk={kiosk} />
                                        </div>
                                        {displayStatus !== "FULL" ? (
                                            <ToggleSwitch
                                                checked={kiosk.status === "ACTIVE"}
                                                disabled={disableConfirmMap[kiosk.docId] ?? false}
                                                onChange={() => toggleKiosk(kiosk)}
                                            />
                                        ) : (
                                            <p className={style["text--eyebrow--caution"]}>꽉참</p>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>
            )}
        </>
    );
};

const CollectButton = ({ loading, onClick }: { loading: boolean; onClick: () => void }) => {
    const [confirming, setConfirming] = useState<boolean>(false);
    const containerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!confirming) return;

        const close = (event: MouseEvent) => {
            if (!containerRef.current?.contains(event.target as Node)) {
                setConfirming(false);
            }
        };

        document.body.addEventListener("click", close);
        return () => document.body.removeEventListener("click", close);
    }, [confirming]);

    return (
        <div ref={containerRef}>
            <button
                type="button"
                className={[style["button--collect"], confirming && style["button--collect--confirm"]].filter(Boolean).join(" ")}
                disabled={loading}
                onClick={() => {
                    if (!confirming) {
                        setConfirming(true);
                        return;
                    }
                    onClick();
                    setConfirming(false);
                }}
            >
                {loading ? "처리 중" : confirming ? "수거 승인하기" : "수거 처리하기"}
            </button>
        </div>
    );
};

const getFilterLabel = (filter: KioskFilter) => {
    const map: Record<KioskFilter, string> = {
        ALL: "전체",
        ACTIVE: "운영중",
        INACTIVE: "운영중지",
        NEAR_FULL: "수거 임박",
        FULL: "수거 필요",
        PENDING: "등록 대기",
    };
    return map[filter];
};

const formatDateTime = (timestamp?: number) => {
    if (!timestamp) return "-";
    return new Date(Number(timestamp)).toLocaleString("ko-KR");
};

const StatusBadge = ({ status }: { status: KioskDisplayStatus }) => {
    const map = {
        ACTIVE: { label: "운영중", className: "status--success" },
        INACTIVE: { label: "운영중지", className: "status--default" },
        NEAR_FULL: { label: "수거임박", className: "status--warning" },
        FULL: { label: "수거필요", className: "status--danger" },
        PENDING: { label: "등록대기", className: "status--info" },
    };
    const item = map[status];
    return <span className={[style["status"], style[item.className]].join(" ")}>{item.label}</span>;
};

const CoordinateBadge = ({ kiosk }: { kiosk: KioskType }) => {
    if (hasKioskCoordinates(kiosk)) return null;

    return (
        <span className={[style["coordinate-badge"], style["coordinate-badge--missing"]].join(" ")}>
            <AlertTriangle size={13} />
            위치 미등록
        </span>
    );
};

const loadKakaoMap = (appKey: string) => {
    if (window.kakao?.maps) {
        return new Promise<any>((resolve) => {
            window.kakao.maps.load(() => resolve(window.kakao));
        });
    }

    if (kakaoMapLoader) return kakaoMapLoader;

    kakaoMapLoader = new Promise((resolve, reject) => {
        const existingScript = document.getElementById("kakao-map-sdk") as HTMLScriptElement | null;

        const handleLoad = () => {
            if (!window.kakao?.maps) {
                reject(new Error("Kakao map SDK was not loaded."));
                return;
            }
            window.kakao.maps.load(() => resolve(window.kakao));
        };

        if (existingScript) {
            existingScript.addEventListener("load", handleLoad, { once: true });
            existingScript.addEventListener("error", () => reject(new Error("Kakao map SDK load failed.")), { once: true });
            return;
        }

        const script = document.createElement("script");
        script.id = "kakao-map-sdk";
        script.async = true;
        script.src = `https://dapi.kakao.com/v2/maps/sdk.js?appkey=${appKey}&autoload=false`;
        script.onload = handleLoad;
        script.onerror = () => reject(new Error("Kakao map SDK load failed."));
        document.head.appendChild(script);
    });

    return kakaoMapLoader;
};

const CoordinatePickerMap = ({
    coordinates,
    onChange,
}: {
    coordinates: Coordinates | null;
    onChange: (coordinates: Coordinates) => void;
}) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const mapRef = useRef<any>(null);
    const markerRef = useRef<any>(null);
    const markerDragHandlerRef = useRef<(() => void) | null>(null);
    const kakaoRef = useRef<any>(null);
    const onChangeRef = useRef(onChange);
    const [mapReady, setMapReady] = useState<boolean>(false);
    const [mapError, setMapError] = useState<string>("");

    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    useEffect(() => {
        if (!KAKAO_MAP_APP_KEY) {
            setMapError("지도 앱 키가 없어 숫자로만 좌표를 입력할 수 있습니다.");
            return;
        }

        let cancelled = false;
        let kakaoInstance: any = null;
        let mapInstance: any = null;
        let clickHandler: ((event: any) => void) | null = null;

        loadKakaoMap(KAKAO_MAP_APP_KEY)
            .then((kakao) => {
                if (cancelled || !containerRef.current) return;

                kakaoInstance = kakao;
                kakaoRef.current = kakao;
                const center = coordinates
                    ? new kakao.maps.LatLng(coordinates.lat, coordinates.lng)
                    : new kakao.maps.LatLng(37.5665, 126.978);
                mapInstance = new kakao.maps.Map(containerRef.current, {
                    center,
                    level: coordinates ? 4 : 8,
                });
                mapRef.current = mapInstance;

                clickHandler = (event: any) => {
                    const position = event.latLng;
                    onChangeRef.current({
                        lat: Number(position.getLat().toFixed(6)),
                        lng: Number(position.getLng().toFixed(6)),
                    });
                };
                kakao.maps.event.addListener(mapInstance, "click", clickHandler);

                setMapReady(true);
                setMapError("");
                requestAnimationFrame(() => mapInstance.relayout());
            })
            .catch((error) => {
                console.error(error);
                setMapError("지도를 불러오지 못했습니다. 숫자로 좌표를 직접 입력해주세요.");
            });

        return () => {
            cancelled = true;
            if (kakaoInstance && mapInstance && clickHandler) {
                kakaoInstance.maps.event.removeListener(mapInstance, "click", clickHandler);
            }
            if (kakaoInstance && markerRef.current && markerDragHandlerRef.current) {
                kakaoInstance.maps.event.removeListener(markerRef.current, "dragend", markerDragHandlerRef.current);
            }
            markerRef.current?.setMap(null);
            markerRef.current = null;
            mapRef.current = null;
        };
        // 지도 인스턴스는 모달이 열릴 때 한 번만 생성합니다.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const kakao = kakaoRef.current;
        const map = mapRef.current;
        if (!mapReady || !kakao || !map) return;

        if (!coordinates) {
            if (markerRef.current && markerDragHandlerRef.current) {
                kakao.maps.event.removeListener(markerRef.current, "dragend", markerDragHandlerRef.current);
            }
            markerRef.current?.setMap(null);
            markerRef.current = null;
            markerDragHandlerRef.current = null;
            return;
        }

        const position = new kakao.maps.LatLng(coordinates.lat, coordinates.lng);
        if (!markerRef.current) {
            const marker = new kakao.maps.Marker({
                map,
                position,
                draggable: true,
            });
            const dragHandler = () => {
                const nextPosition = marker.getPosition();
                onChangeRef.current({
                    lat: Number(nextPosition.getLat().toFixed(6)),
                    lng: Number(nextPosition.getLng().toFixed(6)),
                });
            };

            markerRef.current = marker;
            markerDragHandlerRef.current = dragHandler;
            kakao.maps.event.addListener(marker, "dragend", dragHandler);
        } else {
            markerRef.current.setPosition(position);
        }

        map.panTo(position);
    }, [coordinates, mapReady]);

    return (
        <div className={style["coordinate-map-shell"]}>
            <div ref={containerRef} className={style["coordinate-map"]} />
            {!mapReady && (
                <div className={style["coordinate-map-overlay"]}>
                    <MapPin size={20} />
                    <span>{mapError || "지도를 불러오는 중입니다."}</span>
                </div>
            )}
            {mapReady && (
                <span className={style["coordinate-map-hint"]}>
                    {coordinates ? "핀을 드래그하거나 지도를 클릭해 위치를 조정하세요." : "지도를 클릭해 위치를 지정하세요."}
                </span>
            )}
        </div>
    );
};

const getInfoWindowContent = (kiosk: KioskType, onClose: () => void) => {
    const status = getFilterLabel(getKioskDisplayStatus(kiosk));
    const name = escapeHtml(kiosk.name || kiosk.id || "이름 없음");
    const address = escapeHtml(kiosk.address || "주소 없음");
    const id = escapeHtml(kiosk.id || kiosk.docId || "");

    const wrapper = document.createElement("div");
    wrapper.className = style["map-popup"];
    wrapper.innerHTML = `
        <div class="${style["map-popup-head"]}">
            <strong>${name}</strong>
            <button type="button" aria-label="팝업 닫기">×</button>
        </div>
        <span>${status} · ${id}</span>
        <p>${address}</p>
    `;

    wrapper.querySelector("button")?.addEventListener("click", (event) => {
        event.stopPropagation();
        onClose();
    });

    return wrapper;
};

const getMarkerContent = (kiosk: KioskType) => {
    const statusClass = getMarkerStatusClass(getKioskDisplayStatus(kiosk));
    const label = escapeHtml(kiosk.name || kiosk.id || "키오스크");

    const wrapper = document.createElement("button");
    wrapper.type = "button";
    wrapper.className = `${style["map-marker"]} ${style[`map-marker--${statusClass}`]}`;
    wrapper.setAttribute("aria-label", `${label} 위치`);
    wrapper.innerHTML = `<span></span>`;
    return wrapper;
};

const getMarkerStatusClass = (status: KioskDisplayStatus) => {
    if (status === "ACTIVE") return "active";
    if (status === "NEAR_FULL") return "warning";
    if (status === "FULL") return "full";
    if (status === "PENDING") return "pending";
    return "inactive";
};

const escapeHtml = (value: string) =>
    value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");

const EditKiosk = ({ kiosk, required = false, close }: { kiosk: KioskType; required: boolean; close: () => void }) => {
    const init = useRef(true);
    const [operationMode, setOperationMode] = useState<KioskOperationMode | null>(() => resolveOperationMode(kiosk.operationMode));

    const [changed, setChanged] = useState<{ [key: string]: boolean }>({
        name: false,
        address: false,
        message: false,
        maxCapacity: false,
        pointPerPet: false,
        photo: false,
        partner: false,
    });

    const [name, setName] = useState<string>(kiosk.name ? kiosk.name : "");
    const [nameError, setNameError] = useState<string>("");
    useEffect(() => {
        if (init.current) return;

        if (name.trim() && name.trim().length < 2) {
            setNameError(ErrMsg["kiosk/name-invalid"]);
        } else {
            setNameError("");
            setChanged((prev) => ({ ...prev, name: true }));
        }
    }, [name]);

    const [address, setAddress] = useState<string>(kiosk.address ? kiosk.address : "");
    const [addressError, setAddressError] = useState<string>("");
    const initialCoordinates = useMemo(() => getValidCoordinates(kiosk.lat, kiosk.lng), [kiosk.lat, kiosk.lng]);
    const [latitude, setLatitude] = useState<string>(initialCoordinates ? String(initialCoordinates.lat) : "");
    const [longitude, setLongitude] = useState<string>(initialCoordinates ? String(initialCoordinates.lng) : "");
    const [coordinatesTouched, setCoordinatesTouched] = useState<boolean>(false);
    const [coordinateError, setCoordinateError] = useState<string>("");
    const [coordinateLookupLoading, setCoordinateLookupLoading] = useState<boolean>(false);
    const [coordinateLookupError, setCoordinateLookupError] = useState<string>("");
    const coordinateLookupRequestRef = useRef(0);
    const coordinates = useMemo(() => parseCoordinateInput(latitude, longitude), [latitude, longitude]);
    const hasCoordinateInput = Boolean(latitude.trim() || longitude.trim());
    const addressChanged = address.trim() !== (kiosk.address || "").trim();

    const setAddressWithNaverCoordinates = async (data: any) => {
        const selectedAddress = (data?.roadAddress || data?.jibunAddress || data?.address || "").trim();
        const addressCandidates = Array.from(
            new Set(
                [data?.roadAddress, data?.jibunAddress, data?.address]
                    .filter((candidate): candidate is string => typeof candidate === "string")
                    .map((candidate) => candidate.trim())
                    .filter(Boolean)
            )
        );

        setAddress(selectedAddress);
        setAddressError(selectedAddress ? "" : ErrMsg["kiosk/address-invalid"]);
        setCoordinateError("");
        setCoordinateLookupError("");
        setCoordinatesTouched(false);

        if (selectedAddress === (kiosk.address || "").trim() && initialCoordinates) {
            setLatitude(String(initialCoordinates.lat));
            setLongitude(String(initialCoordinates.lng));
        } else {
            setLatitude("");
            setLongitude("");
        }

        if (!selectedAddress || addressCandidates.length === 0) return;

        const requestId = coordinateLookupRequestRef.current + 1;
        coordinateLookupRequestRef.current = requestId;
        setCoordinateLookupLoading(true);

        try {
            const result = await httpsCallable(functions, "getKioskLatLng")({ addresses: addressCandidates });
            if (coordinateLookupRequestRef.current !== requestId) return;

            const resultData = result.data as { lat?: unknown; lng?: unknown };
            const nextCoordinates = getValidCoordinates(resultData.lat, resultData.lng);
            if (!nextCoordinates) throw new Error("Invalid coordinates");

            setLatitude(nextCoordinates.lat.toFixed(6));
            setLongitude(nextCoordinates.lng.toFixed(6));
            setCoordinatesTouched(true);
        } catch (error) {
            if (coordinateLookupRequestRef.current !== requestId) return;
            console.error(error);
            setCoordinateLookupError("주소에서 좌표를 찾지 못했습니다. 직접 입력하거나 지도를 클릭해주세요.");
        } finally {
            if (coordinateLookupRequestRef.current === requestId) {
                setCoordinateLookupLoading(false);
            }
        }
    };

    const cancelCoordinateLookup = () => {
        coordinateLookupRequestRef.current += 1;
        setCoordinateLookupLoading(false);
        setCoordinateLookupError("");
    };

    const setManualCoordinates = (nextCoordinates: Coordinates) => {
        cancelCoordinateLookup();
        setLatitude(nextCoordinates.lat.toFixed(6));
        setLongitude(nextCoordinates.lng.toFixed(6));
        setCoordinatesTouched(true);
        setCoordinateError("");
    };

    useEffect(() => {
        if (init.current) return;

        if (address.trim() && address.trim().length < 2) {
            setAddressError(ErrMsg["kiosk/address-invalid"]);
        } else {
            setAddressError("");
            setChanged((prev) => ({ ...prev, address: true }));
        }
    }, [address]);

    const [message, setMessage] = useState<string>(kiosk.message ? kiosk.message : "");
    useEffect(() => {
        if (init.current) return;
        setChanged((prev) => ({ ...prev, message: true }));
    }, [message]);

    const [maxCapacity, setMaxCapacity] = useState<string>(kiosk.maxCapacity ? String(kiosk.maxCapacity) : "");
    const [maxCapacityError, setMaxCapacityError] = useState<string>("");
    useEffect(() => {
        if (init.current) return;

        if (maxCapacity.trim() && parsePositiveInteger(maxCapacity) === null) {
            setMaxCapacityError(ErrMsg["kiosk/maxCapacity-invalid"]);
        } else {
            setMaxCapacityError("");
            setChanged((prev) => ({ ...prev, maxCapacity: true }));
        }
    }, [maxCapacity]);

    const [pointPerPet, setPointPerPet] = useState<string>(
        (kiosk as any).pointPerPet !== undefined && (kiosk as any).pointPerPet !== null
            ? String((kiosk as any).pointPerPet)
            : ""
    );
    const [pointPerPetError, setPointPerPetError] = useState<string>("");
    useEffect(() => {
        if (init.current) return;

        if (pointPerPet.trim() && parseOptionalNonNegativeNumber(pointPerPet) === null) {
            setPointPerPetError("유효한 포인트 값을 입력하세요");
        } else {
            setPointPerPetError("");
            setChanged((prev) => ({ ...prev, pointPerPet: true }));
        }
    }, [pointPerPet]);

    const [photo, setPhoto] = useState<File | null>(null);
    const [photoUrl] = useState<string>(kiosk.photo ?? ""); // 기존 이미지 URL 저장
    const [photoErr, setPhotoError] = useState<string>("");
    const [progress, setProgress] = useState<number>(0);

    // partner list 및 선택 (빈 값은 없음 처리 — 빈 문자열은 파이어스토어에서 키 삭제로 저장될 수 있음)
    const { partner } = usePartner();
    const [partnerId, setPartnerId] = useState<string>(kiosk.partnerId ? kiosk.partnerId : "");
    const isPartnerReadonly = Boolean(kiosk.partnerId);
    const selectedPartner = partner.find((p) => p.id === partnerId);
    const partnerDisplayName = selectedPartner?.name || partnerId || "파트너 미지정";
    const [partnerIdError, setPartnerIdError] = useState<string>("");

    useEffect(() => {
        if (init.current) return;
        setPartnerIdError("");
        setChanged((prev) => ({ ...prev, partner: true }));
    }, [partnerId]);

    useEffect(() => {
        if (init.current) return;

        if (photo) {
            setPhotoError("");
            setChanged((prev) => ({ ...prev, photo: true }));
        } else {
            setPhotoError("");
        }
    }, [photo, kiosk.docId, kiosk.photo, required]);

    useEffect(() => {
        if (init.current) {
            init.current = false;
        }
    }, [init]);

    const [loading, setLoading] = useState<boolean>(false);
    const [saveError, setSaveError] = useState<string>("");

    const edit = async () => {
        if (loading || coordinateLookupLoading) return;

        const persistedPartnerId = isPartnerReadonly ? kiosk.partnerId : partnerId;
        const normalizedName = name.trim();
        const normalizedAddress = address.trim();
        const parsedMaxCapacity = parsePositiveInteger(maxCapacity);
        const parsedPointPerPet = parseOptionalNonNegativeNumber(pointPerPet);

        setSaveError("");
        if (!operationMode) {
            setSaveError("운영 방식을 선택해주세요.");
            return;
        }

        if (normalizedName.length < 2) {
            setNameError(ErrMsg["kiosk/name-invalid"]);
            return;
        } else if (normalizedAddress.length < 2) {
            setAddressError(ErrMsg["kiosk/address-invalid"]);
            return;
        } else if (parsedMaxCapacity === null) {
            setMaxCapacityError(ErrMsg["kiosk/maxCapacity-invalid"]);
            return;
        } else if (pointPerPet.trim() && parsedPointPerPet === null) {
            setPointPerPetError("유효한 포인트 값을 입력하세요");
            return;
        } else if (required && !persistedPartnerId) {
            setPartnerIdError(ErrMsg["auth/partnerId-invalid"]);
            return;
        }

        if (hasCoordinateInput && !coordinates) {
            setCoordinateError(
                !latitude.trim() || !longitude.trim()
                    ? "위도와 경도를 모두 입력해주세요."
                    : "유효한 좌표를 입력해주세요. 위도는 -90~90, 경도는 -180~180 범위여야 합니다."
            );
            return;
        }

        setLoading(true);

        const data: { [key: string]: string | number } = {};
        data["operationMode"] = operationMode;

        if (required) {
            // 최초 등록 시
            data["status"] = "ACTIVE";
            data["name"] = normalizedName;
            data["address"] = normalizedAddress;
            data["message"] = message;
            data["maxCapacity"] = parsedMaxCapacity;
            if (parsedPointPerPet !== null) data["pointPerPet"] = parsedPointPerPet;
            // kiosk 앱이 등록 요청 시 보낸 partnerId는 승인 과정에서 변경하지 않음
            if (persistedPartnerId) data["partnerId"] = persistedPartnerId;
            data["currentCapacity"] = 0;
            data["timestamp"] = new Date().getTime();
        } else {
            if (changed["name"]) data["name"] = normalizedName;
            if (changed["address"]) data["address"] = normalizedAddress;
            if (changed["message"]) data["message"] = message;
            if (changed["maxCapacity"]) data["maxCapacity"] = parsedMaxCapacity;
            if (changed["pointPerPet"]) data["pointPerPet"] = parsedPointPerPet ?? 0;
            // partner 변경이 감지되었을 때: 빈값이면 필드 삭제, 값이 있으면 값 저장
            if (changed["partner"]) {
                if (partnerId === "") {
                    // firestore에서 해당 키를 삭제하도록 설정
                    (data as any)["partnerId"] = deleteField();
                } else {
                    data["partnerId"] = partnerId;
                }
            }
        }

        if (coordinatesTouched && coordinates) {
            data["lat"] = coordinates.lat;
            data["lng"] = coordinates.lng;
        } else if (addressChanged && (kiosk.lat !== undefined || kiosk.lng !== undefined)) {
            // 주소가 바뀌면 이전 좌표를 제거하고 Functions의 getLatLng가 새 좌표를 설정하게 합니다.
            (data as any)["lat"] = deleteField();
            (data as any)["lng"] = deleteField();
        }

        // 새 이미지가 업로드된 경우에만 처리
        if (photo && changed["photo"]) {
            try {
                let resizedPhoto = await photoResize(photo, "0");

                const storageRef = ref(storage, `kiosk/${kiosk.docId}/image/0`);
                const uploadTask = uploadBytesResumable(storageRef, resizedPhoto);

                await new Promise((resolve, reject) => {
                    uploadTask.on(
                        "state_changed",
                        (snapshot) => {
                            // 업로드 진행률 계산
                            const prog = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                            setProgress(prog);
                        },
                        (error) => {
                            console.error("Upload failed", error);
                            reject(new Error("Upload failed"));
                        },
                        () => {
                            // 업로드 완료 후 다운로드 URL 얻기
                            getDownloadURL(uploadTask.snapshot.ref)
                                .then((downloadURL) => {
                                    data["photo"] = downloadURL;
                                    resolve(true);
                                })
                                .catch(reject);
                        }
                    );
                });
            } catch (err) {
                console.error(err);
                setLoading(false);
                setProgress(0);
                setSaveError("사진을 업로드하지 못했습니다. 잠시 후 다시 시도해주세요.");
                return;
            }
        }

        if (Object.keys(data).length === 0) {
            hide();
            return;
        }

        try {
            await updateDoc(doc(db, "kiosk", kiosk.docId), data);
            setLoading(false);
            hide();
        } catch (err) {
            setLoading(false);
            setSaveError("키오스크 정보를 저장하지 못했습니다. 잠시 후 다시 시도해주세요.");
            console.error(err);
        }
    };

    const [deleteBtn, setDeleteBtn] = useState<"Delete" | "DeleteFill">("Delete");
    const [loadingDelete, setLoadingDelete] = useState<boolean>(false);

    const deleteKiosk = () => {
        if (deleteBtn === "Delete") {
            setDeleteBtn("DeleteFill");
            return;
        }

        setLoadingDelete(true);

        return httpsCallable(
            functions,
            "kioskDelete"
        )({
            docId: kiosk.docId,
        })
            .then((result) => {
                setLoadingDelete(false);
                setDeleteBtn("Delete");

                const data = result.data as {
                    code?: string;
                    message?: string;
                };

                if (data.code) {
                    hide();
                }
            })
            .catch((err) => {
                console.error(err);
                setLoadingDelete(false);
                setDeleteBtn("Delete");

                if (ErrMsg[err.message]) {
                    alert(ErrMsg[err.message]);
                } else {
                    alert("에러가 발생했습니다");
                }

                return;
            });
    };

    const [active, setActive] = useState<boolean>(false);

    useEffect(() => {
        setActive(true);
    }, []);

    const hide = () => {
        setActive(false);
        setTimeout(() => {
            close();
        }, 520);
    };

    return (
        <div
            className={[
                common["container--overlay"],
                style["modal-backdrop"],
                active && common["container--overlay--active"],
            ].join(" ")}
        >
            <div
                className={[
                    common["unit--overlay"],
                    style["modal"],
                    active && common["unit--overlay--active"],
                ].join(" ")}
                role="dialog"
                aria-modal="true"
                aria-labelledby="kiosk-modal-title"
            >
                <div className={style["modal-header"]}>
                    <div>
                        <span className={style["modal-kicker"]}>{required ? "신규 키오스크 등록" : "키오스크 정보 수정"}</span>
                        <h4 id="kiosk-modal-title" className={style["modal-title"]}>
                            키오스크 관리
                        </h4>
                        <p className={style["modal-copy"]}>
                            기본 정보와 사진, 최대 투입량을 관리합니다.
                        </p>
                    </div>
                    <button type="button" className={style["icon-button"]} aria-label="닫기" onClick={hide}>
                        <X size={18} />
                    </button>
                </div>

                <div className={[common["wrapper--overlay"], style["modal-body"]].join(" ")}>
                    <div className={style["modal-grid"]}>
                        <section className={style["form-section"]}>
                            <div className={style["section-head"]}>
                                <h5>기본 정보 ({kiosk.id})</h5>
                                <p>키오스크 목록과 앱 화면에 표시되는 정보입니다.</p>
                            </div>
                            <div className={style["unit--overlay--inputs"]}>
                                <div className={style["partner-row"]}>
                                    <span className={style["partner-label"]}>
                                        파트너
                                        <em className={style["required-mark"]}>*</em>
                                    </span>
                                    <div className={style["partner-value"]}>
                                        {isPartnerReadonly ? (
                                            <div className={style["partner-readonly"]}>
                                                <span>{partnerDisplayName}</span>
                                                {partnerId && partnerDisplayName !== partnerId && <small>{partnerId}</small>}
                                            </div>
                                        ) : (
                                            <SelectorInput
                                                placeholder="파트너사 선택"
                                                value={partnerId || ""}
                                                setValue={setPartnerId}
                                                error={partnerIdError}
                                            >
                                                {partner.map((p) => (
                                                    <option key={p.docId} value={p.id}>
                                                        {p.name}
                                                    </option>
                                                ))}
                                            </SelectorInput>
                                        )}
                                    </div>
                                </div>
                                <BlockInput
                                    type="text"
                                    placeholder="제목"
                                    value={name}
                                    setValue={setName}
                                    error={nameError}
                                    requiredMark
                                />

                                <AddressInput
                                    placeholder="주소"
                                    value={address}
                                    setValue={setAddress}
                                    onComplete={setAddressWithNaverCoordinates}
                                    error={addressError}
                                    requiredMark
                                />

                                <BlockInput
                                    type="text"
                                    placeholder="사용 불가 시 표시 메시지"
                                    value={message}
                                    setValue={setMessage}
                                />
                            </div>
                        </section>

                        <section className={style["form-section"]}>
                            <div className={style["section-head"]}>
                                <h5>투입 정보</h5>
                                <p>수거 판단에 사용할 기준 용량입니다.</p>
                            </div>
                            <KioskOperationModeInput value={operationMode} onChange={setOperationMode} disabled={loading} />
                            <div className={style["unit--overlay--inputs"]}>
                                <BlockInput
                                    type="number"
                                    placeholder="병당 적립 포인트"
                                    value={pointPerPet}
                                    setValue={setPointPerPet}
                                    error={pointPerPetError}
                                />
                                <BlockInput
                                    type="number"
                                    placeholder="최대 투입 개수"
                                    value={maxCapacity}
                                    setValue={setMaxCapacity}
                                    error={maxCapacityError}
                                    requiredMark
                                />
                            </div>
                        </section>

                        <section className={[style["form-section"], style["form-section--danger"]].join(" ")}>
                            <div className={style["section-head"]}>
                                <h5>삭제</h5>
                                <p>삭제는 한 번 더 확인한 뒤 실행됩니다.</p>
                            </div>
                            <button
                                type="button"
                                className={[
                                    style["button--danger"],
                                    deleteBtn === "DeleteFill" && style["button--danger-confirm"],
                                ].join(" ")}
                                onClick={deleteKiosk}
                            >
                                <Trash2 size={16} />
                                <span>
                                    {loadingDelete
                                        ? "삭제 중"
                                        : deleteBtn === "Delete"
                                          ? "삭제하기"
                                          : "삭제 확인하기"}
                                </span>
                            </button>
                        </section>
                    </div>

                    <div className={style["modal-side-column"]}>
                        <aside className={style["photo-panel"]}>
                            <div className={style["section-head"]}>
                                <div className={style["photo-title"]}>
                                    <ImageIcon size={18} />
                                    <h5>키오스크 기본 사진 (선택)</h5>
                                </div>
                                <p>실제 지도/상세 화면에서 사용할 대표 이미지입니다.</p>
                            </div>
                            <div className={style["photo-input-wrap"]}>
                                <PhotoInput
                                    photo={photo}
                                    setPhoto={(file) => setPhoto(file)}
                                    progress={progress}
                                    error={photoErr}
                                    existingImageUrl={photoUrl}
                                />
                            </div>
                        </aside>

                        <section className={[style["form-section"], style["coordinate-panel"]].join(" ")}>
                            <div className={[style["section-head"], style["coordinate-section-title"]].join(" ")}>
                                <div className={style["coordinate-title-row"]}>
                                    <div className={style["coordinate-title-label"]}>
                                        <MapPin size={17} />
                                        <h5>위치 좌표</h5>
                                    </div>
                                    {!coordinates && !coordinateLookupLoading && (
                                        <span className={[style["coordinate-badge"], style["coordinate-badge--missing"]].join(" ")}>
                                            <AlertTriangle size={13} />
                                            위치 미등록
                                        </span>
                                    )}
                                </div>
                                <p>주소를 선택하면 좌표와 지도 위치가 자동으로 반영됩니다.</p>
                            </div>

                            {coordinateLookupLoading && (
                                <p className={style["coordinate-lookup-note"]}>선택한 주소의 좌표를 확인하고 있습니다.</p>
                            )}

                            <div className={style["coordinate-input-grid"]}>
                                <label className={style["coordinate-field"]}>
                                    <span>위도</span>
                                    <input
                                        type="number"
                                        inputMode="decimal"
                                        step="0.000001"
                                        min="-90"
                                        max="90"
                                        value={latitude}
                                        placeholder="예: 37.497952"
                                        onChange={(event) => {
                                            cancelCoordinateLookup();
                                            setLatitude(event.target.value);
                                            setCoordinatesTouched(true);
                                            setCoordinateError("");
                                        }}
                                    />
                                </label>
                                <label className={style["coordinate-field"]}>
                                    <span>경도</span>
                                    <input
                                        type="number"
                                        inputMode="decimal"
                                        step="0.000001"
                                        min="-180"
                                        max="180"
                                        value={longitude}
                                        placeholder="예: 127.027926"
                                        onChange={(event) => {
                                            cancelCoordinateLookup();
                                            setLongitude(event.target.value);
                                            setCoordinatesTouched(true);
                                            setCoordinateError("");
                                        }}
                                    />
                                </label>
                            </div>

                            {coordinateError && <p className={style["coordinate-error"]}>{coordinateError}</p>}
                            {coordinateLookupError && <p className={style["coordinate-error"]}>{coordinateLookupError}</p>}

                            <div className={style["coordinate-tools"]}>
                                <span>지도를 클릭하거나 핀을 드래그해 직접 조정할 수 있습니다.</span>
                            </div>

                            <CoordinatePickerMap coordinates={coordinates} onChange={setManualCoordinates} />
                        </section>
                    </div>
                </div>

                <div className={[common["wrapper--overlay--footer"], style["modal-footer"]].join(" ")}>
                    {saveError && (
                        <p className={style["save-error"]} role="alert">
                            {saveError}
                        </p>
                    )}
                    <button type="button" className={style["button--secondary"]} onClick={hide}>
                        취소
                    </button>
                    <button
                        type="button"
                        className={style["button--primary"]}
                        onClick={edit}
                        disabled={loading || coordinateLookupLoading}
                    >
                        {coordinateLookupLoading
                            ? "좌표 확인 중"
                            : loading
                              ? "저장 중"
                              : required
                                ? "등록 승인하기"
                                : "수정하기"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Kiosk;
